/**
 * Billing — balance overview + transaction history.
 *
 * Polled every 60s. Cached separately from /v1/stats/overview because the
 * billing page renders a richer projection than the dashboard widget; on
 * the dashboard the user only cares about the balance number.
 */

import { useQuery } from '@tanstack/react-query';

import { api } from '@/lib/api';
import { getAccessToken } from '@/lib/auth';

export interface BillingOverview {
  balance: number;
  credit_limit: number;
  low_balance_threshold: number;
  balance_updated_at: string;
  spend_last_7d: number;
  avg_spend_per_day: number;
  /** Estimated days of runway. null when there's no spend history yet. */
  runs_out_in_days: number | null;
}

export type TransactionType =
  | 'deposit'
  | 'withdrawal'
  | 'sms_charge'
  | 'voice_charge'
  | 'refund'
  | 'adjustment'
  | 'commission_in'
  | 'commission_out';

export interface TransactionItem {
  id: number;
  uuid: string;
  type: TransactionType;
  /** Signed amount in USD. Positive credits the tenant, negative debits it. */
  amount: number;
  balance_after: number;
  description?: string;
  reference_type?: string;
  reference_id?: number;
  created_at: string;
  created_by_email?: string;
}

export interface TransactionsResponse {
  items: TransactionItem[];
  total: number;
  limit: number;
  offset: number;
}

const BILLING_KEY = ['billing'] as const;

export function useBillingOverview({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: [...BILLING_KEY, 'overview'],
    queryFn: () => api.get<BillingOverview>('/v1/billing/overview'),
    enabled,
    refetchInterval: enabled ? 60_000 : false,
    refetchIntervalInBackground: false,
    staleTime: 30_000,
  });
}

/**
 * Self-view of the plan THIS tenant is on.
 *
 * For a reseller this is the wholesale plan the platform assigned them
 * (issuer_type='platform'). For a customer it is the retail plan their
 * reseller assigned (issuer_type='reseller'). When `plan_id` is null
 * the tenant is on pure pay-as-you-go and the rest of the fields are
 * empty — UI should render a "no plan" state.
 */
export interface MyPlan {
  plan_id: number | null;
  name?: string;
  tagline?: string;
  color?: string;
  monthly_fee: number;
  /** null = "negotiated" / Enterprise plan, no fixed allotment. */
  included_sms?: number | null;
  overage_per_sms: number;
  features: string[];
  /** Display name of the tenant who owns the plan (e.g. "Platform"). */
  issuer_name?: string;
  /** 'platform' | 'reseller' — lets the UI vary copy. */
  issuer_type?: 'platform' | 'reseller' | '';
}

export function useMyPlan() {
  return useQuery({
    queryKey: [...BILLING_KEY, 'my-plan'],
    queryFn: () => api.get<MyPlan>('/v1/billing/my-plan'),
    staleTime: 60_000,
  });
}

export interface VoiceSpendByCard {
  card_name: string;
  calls: number;
  bill_seconds: number;
  charges: number;
  refunds: number;
}

export interface VoiceSpend {
  charges_30d: number;
  refunds_30d: number;
  net_spend_30d: number;
  billable_calls_30d: number;
  billed_seconds_30d: number;
  by_card: VoiceSpendByCard[];
}

export interface VoiceAllotment {
  /** Empty when the tenant has no voice allotment (no plan / plan
   *  doesn't bundle voice). UI hides the widget in that case. */
  plan_name?: string;
  included_minutes: number;
  minutes_used: number;
  minutes_remaining: number;
  /** 0.0–1.0; drives the progress bar. */
  used_ratio: number;
  /** YYYY-MM. */
  period: string;
}

export function useVoiceAllotment({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: [...BILLING_KEY, 'voice-allotment'],
    queryFn: () => api.get<VoiceAllotment>('/v1/billing/voice-allotment'),
    enabled,
    staleTime: 60_000,
  });
}

export function useVoiceSpend({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: [...BILLING_KEY, 'voice-spend'],
    queryFn: () => api.get<VoiceSpend>('/v1/billing/voice-spend'),
    enabled,
    staleTime: 60_000,
  });
}

export function useBillingTransactions({
  limit = 50,
  offset = 0,
}: { limit?: number; offset?: number } = {}) {
  return useQuery({
    queryKey: [...BILLING_KEY, 'transactions', limit, offset],
    queryFn: () =>
      api.get<TransactionsResponse>(
        `/v1/billing/transactions?limit=${limit}&offset=${offset}`,
      ),
    staleTime: 30_000,
  });
}

/** One row of the consolidated (customer-default) transactions view.
 *  kind=transaction → an individual event (deposit, adjustment…);
 *  kind=sms_usage|voice_usage → one net line per day. */
export interface SummaryRow {
  kind: 'transaction' | 'sms_usage' | 'voice_usage';
  type?: string;
  description?: string;
  amount: number;
  item_count?: number;
  balance_after: number;
  at: string;
}

interface SummaryResponse {
  items: SummaryRow[];
  total: number;
}

export function useBillingSummary({ limit = 50, enabled = true }: { limit?: number; enabled?: boolean } = {}) {
  return useQuery({
    queryKey: [...BILLING_KEY, 'transactions-summary', limit],
    queryFn: () => api.get<SummaryResponse>(`/v1/billing/transactions?view=summary&limit=${limit}`),
    staleTime: 30_000,
    enabled,
  });
}

export interface TransactionsRange {
  /** RFC3339 inclusive lower bound. */
  from: string;
  /** RFC3339 exclusive upper bound. */
  to: string;
}

/**
 * Triggers a CSV download for the calling tenant's transaction ledger
 * over the given range. Same blob-fetch + synthetic-anchor pattern as
 * downloadReportCSV — we can't use a plain <a href> because the
 * endpoint requires the Authorization header.
 */
export async function downloadTransactionsCSV(range: TransactionsRange): Promise<void> {
  const apiBase =
    (typeof import.meta.env.VITE_API_BASE_URL === 'string' &&
      import.meta.env.VITE_API_BASE_URL) ||
    'http://localhost:8080';
  const url = `${apiBase}/v1/billing/transactions.csv?from=${encodeURIComponent(range.from)}&to=${encodeURIComponent(range.to)}`;
  const token = getAccessToken();
  const response = await fetch(url, {
    method: 'GET',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!response.ok) {
    throw new Error(`CSV export failed (${response.status})`);
  }
  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = `ezsender-transactions-${range.from.slice(0, 10)}_${range.to.slice(0, 10)}.csv`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
}
